import {
  DmStagingApiError,
  getDmStaging,
  stageDmWorkbook,
  type DmStagingItemContract,
  type DmStagingResponse,
} from '@urbanik/api-client';
import {
  Button,
  CanonicalFilterBar,
  CanonicalMetricCard,
  CanonicalMetricGrid,
  DataTable,
  EmptyState,
  MoneyValue,
  PageSizeSelector,
  Pagination,
  ResultsCounter,
  SearchField,
  SelectField,
  StatePanel,
  StatusBadge,
  canonicalStates,
  type DataColumn,
} from '@urbanik/ui';
import { Database, FileSpreadsheet, Rows3 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { apiBase } from './api-base';
import { useOrganization } from './OrganizationContext';
type ViewState = 'loading' | 'empty' | 'error' | 'forbidden' | 'success';
const stateDef = (kind: Exclude<ViewState, 'success'>) =>
  canonicalStates.find((s) => s.kind === kind)!;
const months = [
  '',
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
];
export function DmStagingExplorerView({ canImport }: { canImport: boolean }) {
  const organization = useOrganization();
  const canTransformHistorical = canImport && organization?.active.code === 'BASELINE_COMIVALLE';
  const [query, setQuery] = useState(''),
    [debounced, setDebounced] = useState(''),
    [year, setYear] = useState(''),
    [month, setMonth] = useState(''),
    [page, setPage] = useState(1),
    [pageSize, setPageSize] = useState(25),
    [data, setData] = useState<DmStagingResponse | null>(null),
    [state, setState] = useState<ViewState>('loading'),
    [attempt, setAttempt] = useState(0),
    [staging, setStaging] = useState(false),
    [notice, setNotice] = useState(''),
    [errorMessage, setErrorMessage] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebounced(query);
      setPage(1);
    }, 250);
    return () => clearTimeout(timer);
  }, [query]);
  useEffect(() => {
    const controller = new AbortController();
    setState('loading');
    getDmStaging(
      apiBase,
      {
        query: debounced,
        ...(year ? { year: Number(year) } : {}),
        ...(month ? { month: Number(month) } : {}),
        page,
        pageSize,
      },
      controller.signal,
    )
      .then((result) => {
        setData(result);
        setState(result.items.length ? 'success' : 'empty');
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        if (error instanceof DmStagingApiError && error.kind === 'unauthorized') {
          location.hash = '#login';
          return;
        }
        setState(
          error instanceof DmStagingApiError && error.kind === 'forbidden' ? 'forbidden' : 'error',
        );
      });
    return () => controller.abort();
  }, [attempt, debounced, month, page, pageSize, year]);
  const stage = () => {
    setStaging(true);
    setNotice('');
    stageDmWorkbook(apiBase)
      .then((result) => {
        setNotice(
          result.idempotent
            ? 'La fuente ya estaba transformada; se conservó el lote existente.'
            : `Se transformaron ${result.batch.rowCount.toLocaleString('es-EC')} celdas DM.`,
        );
        setAttempt((value) => value + 1);
      })
      .catch((error) =>
        setNotice(error instanceof Error ? error.message : 'No pudimos transformar la fuente.'),
      )
      .finally(() => setStaging(false));
  };
  const columns: readonly DataColumn<DmStagingItemContract>[] = [
    {
      id: 'period',
      header: 'Período',
      accessor: (r) => `${r.year}-${String(r.month).padStart(2, '0')}`,
    },
    {
      id: 'member',
      header: 'Socio',
      accessor: (r) => r.memberLabel || r.memberReference,
      cell: (r) => (
        <div>
          <strong>{r.memberLabel || 'Socio sin nombre visible'}</strong>
          <small className="dm-staging-secondary">Referencia {r.memberReference || '—'}</small>
        </div>
      ),
    },
    {
      id: 'amount',
      header: 'Valor DM',
      accessor: (r) => r.amount ?? r.rawValue ?? '',
      cell: (r) =>
        r.amount ? (
          <MoneyValue value={r.amount} />
        ) : (
          <StatusBadge tone="neutral">{r.rawValue || 'Vacío'}</StatusBadge>
        ),
    },
    {
      id: 'origin',
      header: 'Evidencia de origen',
      accessor: (r) => `${r.sourceSheet}!${r.sourceCell}`,
      cell: (r) => (
        <div>
          <strong>{r.sourceReference ?? `${r.sourceSheet}!${r.sourceCell}`}</strong>
          <small className="dm-staging-secondary">
            Hoja {r.sourceSheet} · Celda {r.sourceCell}
          </small>
        </div>
      ),
    },
    {
      id: 'payment',
      header: 'Pago histórico',
      accessor: (r) => r.paymentStatus ?? '',
      cell: (r) => {
        if (!r.paymentStatus) return <StatusBadge tone="neutral">Sin pago monetario</StatusBadge>;
        const status =
          r.paymentStatus === 'allocated'
            ? { tone: 'success' as const, label: 'Aplicado' }
            : r.paymentStatus === 'exception'
              ? { tone: 'danger' as const, label: 'Excepción' }
              : { tone: 'warning' as const, label: 'Pendiente' };
        return (
          <div className="dm-payment-trace">
            <StatusBadge tone={status.tone}>{status.label}</StatusBadge>
            {r.assignedMemberName ? (
              <small>
                {r.assignedMemberName} · Alícuota {r.assignedSlotNumber}
              </small>
            ) : null}
            {r.allocationSummary ? <small>{r.allocationSummary}</small> : null}
            {r.allocatedAmount ? (
              <small>
                Aplicado USD {r.allocatedAmount} · Crédito USD {r.unappliedAmount ?? '0.00'}
              </small>
            ) : null}
          </div>
        );
      },
    },
    {
      id: 'observation',
      header: 'Observación de origen',
      accessor: (r) => r.sourceObservation ?? '',
      cell: (r) => r.sourceObservation || '—',
    },
  ];
  return (
    <section className="dm-staging-view">
      <header className="page-heading">
        <div>
          <p className="kicker">M07.1 · Migración financiera histórica</p>
          <h1>Explorador DM</h1>
          <p>
            Matrices históricas normalizadas por año, mes y socio, con origen exacto de hoja y
            celda.
          </p>
        </div>
        {canTransformHistorical ? (
          <Button onClick={stage} disabled={staging} icon={<FileSpreadsheet aria-hidden="true" />}>
            {staging ? 'Transformando…' : 'Transformar fuente DM'}
          </Button>
        ) : null}
      </header>
      {notice ? (
        <p className="dm-staging-notice" role="status">
          {notice}
        </p>
      ) : null}
      <CanonicalFilterBar>
        <SearchField value={query} onChange={setQuery} />
        <SelectField
          label="Año"
          value={year}
          onValueChange={(value) => {
            setYear(value);
            setPage(1);
          }}
          options={[
            { value: '', label: 'Todos los años' },
            ...(data?.years ?? []).map((value) => ({ value: String(value), label: String(value) })),
          ]}
        />
        <SelectField
          label="Mes"
          value={month}
          onValueChange={(value) => {
            setMonth(value);
            setPage(1);
          }}
          options={[
            { value: '', label: 'Todos los meses' },
            ...months.slice(1).map((label, index) => ({ value: String(index + 1), label })),
          ]}
        />
      </CanonicalFilterBar>
      {data?.batch ? (
        <CanonicalMetricGrid>
          <CanonicalMetricCard
            label="Celdas normalizadas"
            value={data.batch.rowCount}
            icon={<Rows3 aria-hidden="true" />}
          />
          <CanonicalMetricCard
            label="Cobertura"
            value={`${data.batch.yearFrom}–${data.batch.yearTo}`}
            icon={<Database aria-hidden="true" />}
          />
          <CanonicalMetricCard
            label="Fuente"
            value="XLSM"
            detail={data.batch.sourceFile}
            icon={<FileSpreadsheet aria-hidden="true" />}
          />
        </CanonicalMetricGrid>
      ) : null}
      {state !== 'success' ? (
        state === 'error' ? (
          <StatePanel
            state={{
              ...stateDef('error'),
              message: errorMessage || 'No pudimos consultar los datos DM de la entidad activa.',
            }}
            onAction={() => setAttempt((value) => value + 1)}
          />
        ) : (
          <StatePanel
            state={
              state === 'empty'
                ? {
                    ...stateDef('empty'),
                    title: 'Esta entidad aún no tiene datos DM',
                    message:
                      'Los datos históricos de COMIVALLE no se comparten. Cuando esta entidad incorpore una fuente financiera propia, aparecerá aquí.',
                  }
                : stateDef(state)
            }
          />
        )
      ) : data ? (
        <section className="obligation-table-card">
          <DataTable
            paginate={false}
            rows={data.items}
            columns={columns}
            visibleColumnIds={new Set(columns.map((c) => c.id))}
            sort={{ columnId: 'period', direction: 'descending' }}
            onSort={() => {}}
            empty={
              <EmptyState
                title="Sin filas DM"
                description="Ajusta los filtros o transforma la fuente histórica."
              />
            }
          />
          <footer>
            <ResultsCounter
              from={(data.page - 1) * data.pageSize + 1}
              to={(data.page - 1) * data.pageSize + data.items.length}
              total={data.totalItems}
              noun="filas"
            />
            <Pagination page={data.page} pages={Math.max(1, data.totalPages)} onChange={setPage} />
            <PageSizeSelector
              value={pageSize}
              options={[10, 25, 50, 100]}
              onChange={(value) => {
                setPageSize(value);
                setPage(1);
              }}
            />
          </footer>
        </section>
      ) : null}
    </section>
  );
}
